import { useCallback, useState } from "react";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { checkInvalidCreateOnSchedule } from "../../../components/Schedule/functions/schedule.functions";
import { resetBookingFormForNewVisitorBooking } from "../../Admin/functions/reset-booking-form-for-new-visitor-booking.function";
import { primeBookingFormForMode } from "../../Booking-Form/functions/prime-booking-form-for-mode.function";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { setInputs } from "../../Booking-Form/slices/booking.slice";
import { handleMultiDeleteBookings } from "../../Schedule/schedule.functions";
import { ScheduleMode } from "../../Schedule/partial/ScheduleModeToggle.partial";
import type { LocationUsage } from "../../Reports/typings/reports.types";
import { useRemoteInactivateBookings } from "../../../hooks/Remote/Booking/useRemoteInactivateBookings";
import { quickAutomatedPlaceBooking } from "../../../utils/axios-requests";
import type {
  Dashboard2DayBookingSummary,
  Dashboard2WeekDay,
  DayWorkStatusKind
} from "../types/dashboard2-work-status.types";
import { resolveDashboard2QuickBookTimes } from "../utils/dashboard2-quick-book-times.utils";
import {
  findScheduleById,
  schedulesForUserOnDay,
  selectPrimaryWorkplaceBookingForUserOnDay
} from "../utils/dashboard2-schedule.utils";
import { isDashboard2ClearableWorkStatusBooking } from "../utils/dashboard2-primary-booking.utils";
import {
  isWeekDayKeyInFuture,
  type WeekDayCardActionKind
} from "../utils/dashboard2-week-day-card.utils";

type CreateOnCalendarState = {
  start: DateTime | null;
  end: DateTime | null;
  open: boolean;
};

type FloorPlanState = {
  bookingId: number;
  bookingType: BookingType;
  highlight?: number;
  open: boolean;
};

type Options = {
  schedules: BookingScheduleInterface[];
  currentUserId: string;
  favoriteLocation?: LocationUsage;
  mobileWorkingLocation?: LocationUsage;
  mobileWorkingEnabled?: boolean;
  refetchSchedules: () => void;
  onQuickBookSuccess: () => void;
  scheduleMode: ScheduleMode;
};

export function useDashboard2StatusMutations({
  schedules,
  currentUserId,
  favoriteLocation,
  mobileWorkingLocation,
  mobileWorkingEnabled = true,
  refetchSchedules,
  onQuickBookSuccess,
  scheduleMode
}: Options) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    settings: { standardStartTime, standardEndTime },
    userInformation: {
      sub,
      firstName,
      surname,
      email,
      company: {
        meta: { bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed }
      }
    }
  } = useSelector((state: RootState) => state.login);

  const { mutateAsyncSafe: inactivateBookingsAsync } = useRemoteInactivateBookings();
  const { mutateAsyncSafe: inactivateBookingsSilentAsync } = useRemoteInactivateBookings("");

  const [selectedBooking, setSelectedBooking] = useState<{
    schedule: BookingScheduleInterface | null;
    open: boolean;
  }>({ schedule: null, open: false });

  const [floorPlan, setFloorPlan] = useState<FloorPlanState>({
    bookingId: 0,
    bookingType: BookingType.WORKPLACE,
    open: false
  });

  const [isCreateOnCalendar, setIsCreateOnCalendar] = useState<CreateOnCalendarState>({
    start: null,
    end: null,
    open: false
  });
  const [isStatusMutationPending, setIsStatusMutationPending] = useState(false);

  const refreshAfterMutation = useCallback(() => {
    onQuickBookSuccess();
  }, [onQuickBookSuccess]);

  const performAutoInstantBook = useCallback(
    async (
      day: DateTime,
      isMobileWorking: boolean,
      options?: { suppressSuccessSnackbar?: boolean }
    ): Promise<boolean> => {
      if (isMobileWorking && !mobileWorkingEnabled) {
        return false;
      }

      const bookLocation = isMobileWorking ? mobileWorkingLocation : favoriteLocation;

      if (!bookLocation?.locationInventoryId) {
        enqueueSnackbar(t("There was a booking error"), { variant: "error" });
        return false;
      }

      const bookingDate = day.toFormat("yyyy-MM-dd");
      const { startTime, endTime } = resolveDashboard2QuickBookTimes({
        bookingDate,
        standardStartTime,
        standardEndTime,
        mode: BookingType.WORKPLACE
      });

      try {
        await quickAutomatedPlaceBooking({
          users: [
            {
              userId: sub,
              isExternal: false,
              isMobileWorking
            }
          ],
          bookingType: BookingType.WORKPLACE,
          startDate: bookingDate,
          endDate: bookingDate,
          startTime,
          endTime,
          locationInventoryId: bookLocation.locationInventoryId
        });
        if (!options?.suppressSuccessSnackbar) {
          enqueueSnackbar(t("Successfully booked the workplace."), { variant: "success" });
        }
        refreshAfterMutation();
        return true;
      } catch {
        enqueueSnackbar(t("There was a booking error"), { variant: "error" });
        return false;
      }
    },
    [
      enqueueSnackbar,
      favoriteLocation,
      mobileWorkingEnabled,
      mobileWorkingLocation,
      refreshAfterMutation,
      standardEndTime,
      standardStartTime,
      sub,
      t
    ]
  );

  const clearWorkplaceBookingsForDay = useCallback(
    async (day: DateTime, options?: { refreshAfter?: boolean; silent?: boolean }) => {
      const refreshAfter = options?.refreshAfter ?? true;
      const inactivate = options?.silent ? inactivateBookingsSilentAsync : inactivateBookingsAsync;
      const dayBookings = schedulesForUserOnDay(schedules, currentUserId, day).filter(
        isDashboard2ClearableWorkStatusBooking
      );

      if (dayBookings.length === 0) {
        return;
      }

      await new Promise<void>(resolve => {
        handleMultiDeleteBookings({
          selectedSchedules: dayBookings.map(schedule => ({
            id: schedule.id,
            bookingType: schedule.bookingType as BookingType
          })),
          inactivateBookings: params => {
            inactivate(params).finally(() => {
              resolve();
            });
          }
        });
      });

      if (refreshAfter) {
        refreshAfterMutation();
      }
    },
    [
      currentUserId,
      inactivateBookingsAsync,
      inactivateBookingsSilentAsync,
      refreshAfterMutation,
      schedules
    ]
  );

  const applyWorkStatusBooking = useCallback(
    async (day: DateTime, isMobileWorking: boolean) => {
      if (isMobileWorking && !mobileWorkingEnabled) {
        return;
      }

      if (isWeekDayKeyInFuture(day.toFormat("yyyy-MM-dd"))) {
        await clearWorkplaceBookingsForDay(day, { refreshAfter: false, silent: true });
      }

      const booked = await performAutoInstantBook(day, isMobileWorking, {
        suppressSuccessSnackbar: true
      });

      if (booked) {
        enqueueSnackbar(t("_dashboard2_workStatusUpdated"), { variant: "success" });
      }
    },
    [clearWorkplaceBookingsForDay, enqueueSnackbar, mobileWorkingEnabled, performAutoInstantBook, t]
  );

  const handleSelectStatus = useCallback(
    async (kind: DayWorkStatusKind, day: DateTime) => {
      if (isStatusMutationPending) {
        return;
      }

      setIsStatusMutationPending(true);

      try {
        if (kind === "office") {
          await applyWorkStatusBooking(day, false);
          return;
        }

        if (kind === "mobile_working") {
          await applyWorkStatusBooking(day, true);
          return;
        }

        if (kind === "unplanned") {
          await clearWorkplaceBookingsForDay(day);
        }
      } finally {
        setIsStatusMutationPending(false);
      }
    },
    [applyWorkStatusBooking, clearWorkplaceBookingsForDay, isStatusMutationPending]
  );

  const openPlanDayDialog = useCallback(
    (day: DateTime) => {
      const bookingStartDate = day.startOf("day");
      const bookingEndDate = bookingStartDate;

      if (
        checkInvalidCreateOnSchedule(
          bookingStartDate,
          bookingsOnSaturdaysAllowed,
          bookingsOnSundaysAllowed,
          enqueueSnackbar,
          t
        )
      ) {
        return;
      }

      setIsCreateOnCalendar({
        start: bookingStartDate,
        end: bookingEndDate,
        open: true
      });
      dispatch(
        setInputs({
          bookingFrom: bookingStartDate.toFormat("yyyy-MM-dd"),
          bookingTo: bookingEndDate.toFormat("yyyy-MM-dd")
        })
      );
    },
    [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed, dispatch, enqueueSnackbar, t]
  );

  const handleWeekDayAction = useCallback(
    async (actionKind: WeekDayCardActionKind, day: Dashboard2WeekDay) => {
      if (isStatusMutationPending && actionKind === "book_place") {
        return;
      }

      const dayDate = DateTime.fromISO(day.dayKey);

      if (actionKind === "plan_day") {
        openPlanDayDialog(dayDate);
        return;
      }

      if (actionKind === "book_place") {
        await applyWorkStatusBooking(dayDate, false);
        return;
      }

      if (actionKind === "floor_plan") {
        const primary = selectPrimaryWorkplaceBookingForUserOnDay(
          schedules,
          currentUserId,
          dayDate
        );

        if (!primary) {
          return;
        }

        setFloorPlan({
          open: true,
          bookingId: primary.id,
          bookingType: primary.bookingType as BookingType,
          highlight: primary.bookingInventoryId
        });
      }
    },
    [applyWorkStatusBooking, currentUserId, isStatusMutationPending, openPlanDayDialog, schedules]
  );

  const handleBookingSummaryClick = useCallback(
    (booking: Dashboard2DayBookingSummary) => {
      const schedule = findScheduleById(schedules, booking.id);

      if (!schedule) {
        return;
      }

      setSelectedBooking({ schedule, open: true });
    },
    [schedules]
  );

  const handleScheduleDetailClick = useCallback((schedule: BookingScheduleInterface) => {
    setSelectedBooking({ schedule, open: true });
  }, []);

  const handleCloseBookingDetail = useCallback(() => {
    setSelectedBooking({ schedule: null, open: false });
  }, []);

  const handleCloseFloorPlan = useCallback(() => {
    setFloorPlan(previous => ({ ...previous, open: false }));
  }, []);

  const openFloorPlanFromSchedule = useCallback((schedule: BookingScheduleInterface) => {
    setFloorPlan({
      open: true,
      bookingId: schedule.id,
      bookingType: schedule.bookingType as BookingType,
      highlight: schedule.bookingInventoryId
    });
  }, []);

  const handleCloseCreateDialog = useCallback(() => {
    setIsCreateOnCalendar({ start: null, end: null, open: false });
    refetchSchedules();
  }, [refetchSchedules]);

  const handleBookAVisitor = useCallback(() => {
    resetBookingFormForNewVisitorBooking(dispatch, {
      sub,
      firstName,
      surname,
      email
    });
    history.push("/booking/1");
  }, [dispatch, email, firstName, history, sub, surname]);

  const handleBookPlaceType = useCallback(
    (bookingType: BookingType) => {
      primeBookingFormForMode(dispatch, { sub, firstName, surname, email }, bookingType);
      history.push("/booking/1");
    },
    [dispatch, email, firstName, history, sub, surname]
  );

  return {
    handleSelectStatus,
    handleWeekDayAction,
    handleBookingSummaryClick,
    handleScheduleDetailClick,
    handleBookAVisitor,
    handleBookPlaceType,
    openPlanDayDialog,
    isStatusMutationPending,
    selectedBooking,
    handleCloseBookingDetail,
    floorPlan,
    handleCloseFloorPlan,
    openFloorPlanFromSchedule,
    isCreateOnCalendar,
    handleCloseCreateDialog,
    scheduleMode
  };
}
